import { Subscription } from 'rxjs';
import { OnDestroy, Output, Component, OnInit, EventEmitter, ChangeDetectionStrategy, inject } from '@angular/core';
import { ConsumerPortalConfig } from '../../../../models/consumer-portal-config';
import { TranslateService } from '@ngx-translate/core';
import { AdminRole } from '@por/consumer-login/shared-lib';
import { ComponentsToLoad } from '../../../../enums/components-to-load.enum';
import { Navigation } from '../../../../models/navigation.model';
import { Menu as AdminMenu } from '../../../../models/consumer-portal-admin-config';
import { AppFacadeService } from '../../../../services/app-facade.service';
import { AppMediatorService } from '../../../../services/app-mediator.service';
type Menus = Partial<AdminMenu> | undefined | null;
@Component({
    standalone: false,
    selector: 'por-admin-page-sidebar',
    templateUrl: './admin-page-sidebar.component.html',
    styleUrls: ['./admin-page-sidebar.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class AdminPageSidebarComponent implements OnInit, OnDestroy {
    private readonly translateService = inject(TranslateService);
    readonly appFacadeService = inject(AppFacadeService);
    private readonly appMediatorService = inject(AppMediatorService);

    @Output() readonly getActiveTab = new EventEmitter();

    inputs: ConsumerPortalConfig | undefined;
    menu: Menus;
    navigations: Navigation[] = [];
    private readonly allNavigations: Navigation[] = [];
    subscriptions: Subscription[] = [];

    ngOnDestroy(): void {
        this.subscriptions.map(sub => sub.unsubscribe());
    }

    ngOnInit(): void {
        const lang = this.appMediatorService.localStorageService.selectedContentLanguage;
        if (lang && lang !== this.translateService.getCurrentLang()) {
            this.translateService.use(lang);
        }

        [
            this.showTab(ComponentsToLoad.AdminOverview) && {
                tab: ComponentsToLoad.AdminOverview,
                text: this.translateService.instant('AdminNavOverview'),
                params: []
            },
            this.showTab(ComponentsToLoad.AdminDifference) && {
                tab: ComponentsToLoad.AdminDifference,
                text: this.translateService.instant('AdminNavDiff'),
                params: []
            },
            this.appFacadeService.versionToggleService.isBasicVersion() && {
                tab: ComponentsToLoad.AdminSetup,
                text: this.translateService.instant('AdminNavBasicSetup')
            },
            this.appFacadeService.versionToggleService.isAdvanceVersion() && {
                tab: ComponentsToLoad.AdminSetup,
                text: this.translateService.instant('AdminNavAdvSetup'),
                params: []
            },
            {
                tab: ComponentsToLoad.AdminAccountList,
                text: this.translateService.instant('AdminNavAccountsList'),
                params: []
            },
            this.showTab(ComponentsToLoad.AdminAnalytics) && {
                tab: ComponentsToLoad.AdminAnalytics,
                text: this.translateService.instant('AdminAnalytics'),
                params: []
            }
        ].map(nav => {
            if (nav) {
                this.allNavigations.push(nav);
            }
        });

        // Restrict the menu to the pages granted by the user's Role (empty → show all).
        this.subscriptions.push(
            this.appFacadeService.getAdminRoles().subscribe(roles => {
                this.navigations = roles.length ? this.allNavigations.filter(nav => roles.includes(nav.tab as unknown as AdminRole)) : this.allNavigations;
            })
        );
    }

    toggleTab(tab: ComponentsToLoad | undefined) {
        if (tab) {
            this.appFacadeService.setActiveTab(tab);
            this.getActiveTab.emit(tab);
        }
    }

    private showTab(tabName: ComponentsToLoad): boolean {
        switch (tabName) {
            case ComponentsToLoad.AdminOverview:
                return !this.appFacadeService.featureToggleService.isSyrinx();
            case ComponentsToLoad.AdminDifference:
                return !this.appFacadeService.featureToggleService.isSyrinx();
            case ComponentsToLoad.AdminAccountList:
                return true; // Always show this tab

            case ComponentsToLoad.AdminAnalytics:
                return !this.appFacadeService.featureToggleService.isSyrinx() && this.appFacadeService.versionToggleService.isAdvanceVersion();

            default:
                return false; // Hide all other tabs by default
        }
    }
}
